import React from 'react';
import { NavLink } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, BarChart3, Shield, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const tools = [
  {
    title: 'AI Assistants',
    description: 'Access all AI-powered features and smart automation tools',
    icon: Brain,
    url: '/dashboard-sidebar/ai-assistants',
    color: 'from-blue-500 to-purple-600'
  },
  {
    title: 'AI Laboratory',
    description: 'Advanced AI testing, analysis tools, and experimental features',
    icon: Wand2,
    url: '/dashboard-sidebar/ai-lab',
    color: 'from-purple-500 to-pink-600'
  },
  {
    title: 'Analytics',
    description: 'View performance metrics, insights, and detailed reports',
    icon: BarChart3,
    url: '/dashboard-sidebar/analytics',
    color: 'from-green-500 to-teal-600'
  },
  {
    title: 'Content Moderation',
    description: 'Review, moderate, and manage your content effectively',
    icon: Shield,
    url: '/dashboard-sidebar/moderation',
    color: 'from-orange-500 to-red-600'
  }
];

const Tools = () => {
  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Tools</h1>
          <p className="text-gray-600">
            Access all your AI-powered tools, analytics, and moderation features in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((tool) => (
            <Card key={tool.title} className="group hover:shadow-lg transition-all duration-200 border-0 shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-lg bg-gradient-to-r ${tool.color} shadow-lg`}>
                    <tool.icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl text-gray-900">{tool.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="text-gray-600 mb-4 line-clamp-2">
                  {tool.description}
                </CardDescription>
                <Button asChild variant="outline" className="w-full group-hover:border-primary group-hover:text-primary transition-colors">
                  <NavLink to={tool.url}>
                    Open {tool.title}
                  </NavLink>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 border">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Streamlined Workflow</h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              All your essential tools are organized here to help you work more efficiently. 
              From AI-powered content analysis to detailed analytics and moderation features.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-sm">
                <Brain className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">AI-Powered</span>
              </div>
              <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-sm">
                <BarChart3 className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Data-Driven</span>
              </div>
              <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 shadow-sm">
                <Shield className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Secure</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tools;